import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { Drawer } from './ui/Drawer';
import { api } from '../lib/api';

type GlKind = 'asset' | 'liability' | 'equity' | 'revenue' | 'expense';

interface GlAccount {
  id: string;
  code: string;
  name: string;
  kind: GlKind;
  is_active: boolean;
}

const inputCn =
  'w-full px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 ' +
  'bg-white dark:bg-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-500';

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <label className="text-[11px] uppercase tracking-wide text-ink-500">{label}</label>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

export function GlAccountUpsertDrawer({
  wsId,
  open,
  onOpenChange,
}: {
  wsId: string;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<GlKind>('asset');
  const [parentId, setParentId] = useState<string>('');
  const [isActive, setIsActive] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Existing accounts feed the parent dropdown.
  const existing = useQuery<GlAccount[]>({
    queryKey: ['gl-accounts', wsId],
    enabled: !!wsId,
    queryFn: async () => (await api.get<GlAccount[]>(`/workspaces/${wsId}/ledger/accounts`)).data,
  });

  const upsert = useMutation({
    mutationFn: async () =>
      (
        await api.post(`/workspaces/${wsId}/ledger/accounts`, {
          code,
          name,
          kind,
          parentId: parentId || undefined,
          isActive,
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gl-accounts', wsId] });
      setCode('');
      setName('');
      setParentId('');
      setIsActive(true);
      onOpenChange(false);
    },
    onError: (err: any) => setError(err?.response?.data?.message ?? err?.message ?? 'Save failed'),
  });
  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title={t('ledger.addAccount')}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium hover:bg-ink-50 dark:hover:bg-ink-900"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              if (!code.trim() || !name.trim()) {
                setError(t('ledger.errorCodeNameRequired'));
                return;
              }
              upsert.mutate();
            }}
            disabled={upsert.isPending}
            className="px-4 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50"
          >
            {t('common.save')}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="text-sm text-warn-600 rounded-lg border border-warn-300 bg-warn-50 dark:bg-warn-900/20 px-3 py-2">
            {error}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <FormField label={t('ledger.code')}>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="1110"
              className={inputCn}
            />
          </FormField>
          <FormField label={t('ledger.kind')}>
            <select value={kind} onChange={(e) => setKind(e.target.value as GlKind)} className={inputCn}>
              {(['asset', 'liability', 'equity', 'revenue', 'expense'] as GlKind[]).map((k) => (
                <option key={k} value={k}>
                  {t(`ledger.create.kinds.${k}`)}
                </option>
              ))}
            </select>
          </FormField>
        </div>
        <FormField label={t('ledger.name')}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="現金 / Cash"
            className={inputCn}
          />
        </FormField>
        <FormField label={t('ledger.parent')}>
          <select value={parentId} onChange={(e) => setParentId(e.target.value)} className={inputCn}>
            <option value="">{t('ledger.noParent')}</option>
            {(existing.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} · {p.name}
              </option>
            ))}
          </select>
        </FormField>
        <div className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            id="new-acct-active"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="h-4 w-4 rounded border-ink-300 dark:border-ink-700 accent-accent-600"
          />
          <label htmlFor="new-acct-active">{t('ledger.activeFlag')}</label>
        </div>
        <p className="text-[11px] text-ink-500">
          {t('ledger.upsertHint')}
        </p>
      </div>
    </Drawer>
  );
}

interface DraftLine {
  accountId: string;
  debit: string;
  credit: string;
  memo: string;
}

function emptyJournalLine(): DraftLine {
  return { accountId: '', debit: '', credit: '', memo: '' };
}

export function JournalPostDrawer({
  wsId,
  open,
  onOpenChange,
}: {
  wsId: string;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const accounts = useQuery<GlAccount[]>({
    queryKey: ['gl-accounts', wsId],
    enabled: !!wsId,
    queryFn: async () => (await api.get<GlAccount[]>(`/workspaces/${wsId}/ledger/accounts`)).data,
  });

  const today = new Date().toISOString().slice(0, 10);
  const [postedOn, setPostedOn] = useState(today);
  const [kind, setKind] = useState('manual');
  const [memo, setMemo] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([emptyJournalLine(), emptyJournalLine()]);
  const [error, setError] = useState<string | null>(null);

  const totalDebit = lines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
  const balanced = totalDebit === totalCredit && totalDebit > 0;

  const post = useMutation({
    mutationFn: async () => {
      const payload = {
        postedOn,
        kind,
        memo: memo.trim() || undefined,
        lines: lines
          .filter((l) => l.accountId && (Number(l.debit) > 0 || Number(l.credit) > 0))
          .map((l) => {
            const debit = Number(l.debit) || 0;
            const credit = Number(l.credit) || 0;
            return {
              accountId: l.accountId,
              debitCents: debit,
              creditCents: credit,
              // Backend requires currency + baseAmountCents per line. For a
              // single-entity JPY workspace they collapse to one number; this
              // matches the validation in LedgerService.postJournal.
              currency: 'JPY',
              baseAmountCents: debit > 0 ? debit : credit,
              memo: l.memo.trim() || undefined,
            };
          }),
      };
      return (await api.post(`/workspaces/${wsId}/ledger/journals`, payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gl-journals', wsId] });
      setMemo('');
      setLines([emptyJournalLine(), emptyJournalLine()]);
      onOpenChange(false);
    },
    onError: (err: any) => setError(err?.response?.data?.message ?? err?.message ?? 'Post failed'),
  });

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={t('ledger.postJournal')}
      footer={
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs">
            <span className={balanced ? 'text-emerald-600' : 'text-warn-600'}>
              {t('ledger.debit')}: {totalDebit.toLocaleString()} ·{' '}
              {t('ledger.credit')}: {totalCredit.toLocaleString()}
            </span>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium hover:bg-ink-50 dark:hover:bg-ink-900"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={() => {
                setError(null);
                if (!balanced) {
                  setError(t('ledger.errorUnbalanced'));
                  return;
                }
                post.mutate();
              }}
              disabled={post.isPending || !balanced}
              className="px-4 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50"
            >
              {t('common.save')}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="text-sm text-warn-600 rounded-lg border border-warn-300 bg-warn-50 dark:bg-warn-900/20 px-3 py-2">
            {error}
          </div>
        )}
        <div className="grid grid-cols-3 gap-3">
          <FormField label={t('ledger.postedOn')}>
            <input type="date" value={postedOn} onChange={(e) => setPostedOn(e.target.value)} className={inputCn} />
          </FormField>
          <FormField label={t('ledger.journalKind')}>
            <input value={kind} onChange={(e) => setKind(e.target.value)} placeholder="manual" className={inputCn} />
          </FormField>
          <FormField label={t('inbox.field.notes')}>
            <input value={memo} onChange={(e) => setMemo(e.target.value)} className={inputCn} />
          </FormField>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs uppercase tracking-wide text-ink-500">{t('ledger.lines')}</h3>
            <button
              type="button"
              onClick={() => setLines([...lines, emptyJournalLine()])}
              className="inline-flex items-center gap-1 text-xs text-accent-600 hover:text-accent-700"
            >
              <Plus size={12} />
              {t('invoicesAr.addLine')}
            </button>
          </div>
          <div className="space-y-2">
            {lines.map((l, i) => (
              <div
                key={i}
                className="grid grid-cols-12 gap-2 items-start p-2 rounded-lg border border-ink-200 dark:border-ink-800"
              >
                <select
                  value={l.accountId}
                  onChange={(e) =>
                    setLines(lines.map((ln, idx) => (idx === i ? { ...ln, accountId: e.target.value } : ln)))
                  }
                  className={`${inputCn} col-span-4`}
                >
                  <option value="">—</option>
                  {(accounts.data ?? []).map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.code} · {a.name}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  value={l.debit}
                  onChange={(e) =>
                    setLines(lines.map((ln, idx) => (idx === i ? { ...ln, debit: e.target.value } : ln)))
                  }
                  placeholder={t('ledger.debit')}
                  className={`${inputCn} col-span-2 text-right`}
                />
                <input
                  type="number"
                  value={l.credit}
                  onChange={(e) =>
                    setLines(lines.map((ln, idx) => (idx === i ? { ...ln, credit: e.target.value } : ln)))
                  }
                  placeholder={t('ledger.credit')}
                  className={`${inputCn} col-span-2 text-right`}
                />
                <input
                  value={l.memo}
                  onChange={(e) =>
                    setLines(lines.map((ln, idx) => (idx === i ? { ...ln, memo: e.target.value } : ln)))
                  }
                  placeholder={t('inbox.field.notes')}
                  className={`${inputCn} col-span-3`}
                />
                <button
                  type="button"
                  onClick={() => setLines(lines.filter((_, idx) => idx !== i))}
                  disabled={lines.length <= 2}
                  className="col-span-1 p-1.5 rounded-md text-ink-500 hover:text-warn-600 hover:bg-ink-50 dark:hover:bg-ink-900 disabled:opacity-30 disabled:pointer-events-none"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Drawer>
  );
}
